import { AxiosInstance, AxiosResponse } from "axios"
import { useCallback, useState } from "react"
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { StrictOmit } from "../../lib/lang"
import { ApiError, ErrorDisplay, SuccessDisplay } from "./errorTypes"
import { useAxios } from "./useAxios"
import { useApiErrorHandling } from "./useApiErrorHandling"
import { mockServerUrl } from "../../constants/apiUrl"
import { useEnvironment } from "../../modules/environment/envHooks"
import { EnvironmentType } from "../../modules/environment/envTypes"
import { filterEmptyParams } from "./apiUtils"
import { useAppLocalization } from "../../modules/localization/components/AppLocalizationProvider"

interface BaseContext {
    readonly queryClient: QueryClient
}

type ApiCall<TParams extends object, TResponse> = (params: TParams, axios: AxiosInstance) => Promise<TResponse>
type OnMutate<TParams extends object, TContext extends object> = (params: TParams) => Promise<TContext | undefined>
type OnSuccess<TParams extends object, TResponse, TContext extends object> = (
    params: TParams,
    data: TResponse,
    context: (TContext & BaseContext) | BaseContext,
) => Promise<unknown>
type OnError<TParams extends object, TError, TContext extends object> = (
    params: TParams,
    error: TError,
    context: (TContext & BaseContext) | BaseContext,
) => Promise<unknown>
type OnSettled<TParams extends object, TResponse, TError, TContext extends object> = (
    params: TParams,
    data: TResponse | undefined,
    error: TError | null,
    context: (TContext & BaseContext) | BaseContext,
) => Promise<unknown>

type ApiMutationOptions<TParams extends object, TResponse, TError, TContext extends object> = Readonly<{
    onMutate?: OnMutate<TParams, TContext>
    onSuccess?: OnSuccess<TParams, TResponse, TContext>
    onError?: OnError<TParams, TError, TContext>
    onSettled?: OnSettled<TParams, TResponse, TError, TContext>
    authenticated?: boolean
    errorDisplay?: ErrorDisplay
    successDisplay?: SuccessDisplay
    accept?: string
    baseURL?: string
}>

/**
 * Hook for making authenticated API mutations with built-in success/error handling.
 *
 * @example Snackbar on success — automatic via successDisplay option (preferred)
 * ```tsx
 * const { mutate } = useApiMutation(createOrderApi, {
 *   successDisplay: SuccessDisplay.SNACKBAR,
 *   onSuccess: async (params, data, { queryClient }) => {
 *     await queryClient.invalidateQueries({ queryKey: [OrderQuery.LIST] })
 *   },
 * })
 * ```
 *
 * @example Dialog on success (full response data is available in the dialog)
 * ```tsx
 * type CreateOrderResponse = { orderId: string; totalAmount: number }
 *
 * const {
 *   mutate,
 *   successDialogOpen,
 *   successDialogData,  // typed as CreateOrderResponse | null
 *   closeSuccessDialog,
 * } = useApiMutation<CreateOrderParams, CreateOrderResponse>(createOrderApi, {
 *   successDisplay: SuccessDisplay.DIALOG,
 *   onSuccess: async (params, data, { queryClient }) => {
 *     await queryClient.invalidateQueries({ queryKey: [OrderQuery.LIST] })
 *   },
 * })
 *
 * return (
 *   <>
 *     <Button onClick={() => mutate(params)}>Submit</Button>
 *     <OrderSuccessDialog
 *       open={successDialogOpen}
 *       orderId={successDialogData?.orderId}
 *       totalAmount={successDialogData?.totalAmount}
 *       onClose={closeSuccessDialog}
 *     />
 *   </>
 * )
 * ```
 */
export function useApiMutation<TParams extends object, TResponse, TError = unknown, TContext extends object = {}>(
    apiCall: ApiCall<TParams, TResponse>,
    options: ApiMutationOptions<TParams, TResponse, TError, TContext>,
) {
    // TODO: Default error display should not be snackbar
    const {
        onMutate,
        onSuccess,
        onError,
        onSettled,
        authenticated = true,
        errorDisplay = ErrorDisplay.SNACKBAR,
        successDisplay = SuccessDisplay.NONE,
        accept,
        baseURL,
    } = options

    const queryClient = useQueryClient()
    const axiosInstance = useAxios({ baseURL, authenticated, accept })
    const mockAxiosInstance = useAxios({ baseURL: mockServerUrl, authenticated, accept })
    const { handleError } = useApiErrorHandling()
    const { currentEnv } = useEnvironment()
    const { l10n } = useAppLocalization()

    const successSnackbarHandler = useCallback(
        (data: { serviceResponse?: { displayMessage?: string } }) => {
            const message = data?.serviceResponse?.displayMessage ?? l10n.getString("api-call-generic-success")
            enqueueSnackbar({ variant: "success", message })
        },
        [l10n],
    )

    const [successDialogData, setSuccessDialogData] = useState<TResponse | null>(null)

    const successDialogHandler = useCallback((data: TResponse) => {
        setSuccessDialogData(data)
    }, [])

    const closeSuccessDialog = useCallback(() => {
        setSuccessDialogData(null)
    }, [])

    const mutationFn = useCallback(
        async (params: TParams) => {
            // Filter out empty params (undefined, null, empty strings) before making API calls
            const filteredParams = filterEmptyParams(params)

            try {
                return await apiCall(filteredParams, axiosInstance)
            } catch (error) {
                if (error instanceof ApiError && error.originalErrorResponse) {
                    if (shouldRetryWithMock(error.originalErrorResponse, currentEnv.type)) {
                        // Retry with mock URL
                        return await apiCall(filteredParams, mockAxiosInstance)
                    } else throw error
                }
                throw error
            }
        },
        [apiCall, axiosInstance, mockAxiosInstance, currentEnv.type],
    )

    const onMutateFn = useCallback(
        async (params: TParams) => {
            // TODO: Do we need error handling here? Test how Tanstack handles errors from onMutate!
            return await onMutate?.(params)
        },
        [onMutate],
    )

    const onSuccessFn = useCallback(
        async (data: TResponse, params: TParams, context: TContext | undefined) => {
            if (successDisplay === SuccessDisplay.SNACKBAR) {
                successSnackbarHandler(data as { serviceResponse?: { displayMessage?: string } })
            }
            if (successDisplay === SuccessDisplay.DIALOG) {
                successDialogHandler(data)
            }
            try {
                return await onSuccess?.(params, data, { queryClient, ...(context ?? {}) })
            } catch (err) {
                handleError(err, errorDisplay)
            }
            return undefined
        },
        [onSuccess, queryClient, successDisplay, successSnackbarHandler, successDialogHandler],
    )

    const onErrorFn = useCallback(
        async (error: TError, params: TParams, context: TContext | undefined) => {
            if (onError) {
                try {
                    return await onError(params, error, { queryClient, ...(context ?? {}) })
                } catch (err: unknown) {
                    handleError(err, errorDisplay)
                }
            } else {
                handleError(error, errorDisplay)
            }
            return undefined
        },
        [onError, queryClient],
    )

    const onSettledFn = useCallback(
        async (data: TResponse | undefined, error: TError | null, params: TParams, context: TContext | undefined) => {
            if (onSettled) {
                try {
                    return await onSettled(params, data, error, { queryClient, ...(context ?? {}) })
                } catch (err: unknown) {
                    handleError(err, errorDisplay)
                }
            }
            return undefined
        },
        [onSettled, queryClient],
    )

    const mutation = useMutation<TResponse, TError, TParams, TContext>({
        mutationFn,
        onMutate: onMutateFn,
        onSuccess: onSuccessFn,
        onError: onErrorFn,
        onSettled: onSettledFn,
        retry: false,
    })

    return {
        ...mutation,
        successSnackbarHandler,
        successDialogHandler,
        successDialogOpen: successDialogData !== null,
        successDialogData,
        closeSuccessDialog,
    }
}

function shouldRetryWithMock(error: AxiosResponse, currentEnv: EnvironmentType): boolean {
    return (
        currentEnv === EnvironmentType.Local &&
        error?.status === 404 &&
        Boolean(error.headers["content-type"]?.toString().includes("text/html"))
    )
}

type PublicApiMutationOptions<TParams extends object, TResponse, TError, TContext extends object> = StrictOmit<
    ApiMutationOptions<TParams, TResponse, TError, TContext>,
    "authenticated"
>

export function usePublicApiMutation<TParams extends object, TResponse, TError = unknown, TContext extends object = {}>(
    apiCall: ApiCall<TParams, TResponse>,
    options: PublicApiMutationOptions<TParams, TResponse, TError, TContext>,
) {
    return useApiMutation(apiCall, { ...options, authenticated: false })
}
